import axios from "axios";
import Cookies from "js-cookie";
import { forgetSiThoughtTraces } from "./seenSiTraces.js";
import { forgetBcoThoughtTraces } from "./seenBcoTraces.js";

const API_ROOT = process.env.VUE_APP_ROOT_API;
const SESSION_KEY = "auth_session";
const REFRESH_BEFORE_MS = 30 * 60 * 1000;
const ACTIVE_WITHIN_MS = 5 * 60 * 1000;

const client = axios.create({
    baseURL: API_ROOT,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
});

let refreshPromise = null;
let lastActivityAt = Date.now();

const csrfHeaders = () => {
    const token = Cookies.get("nba_csrf");
    return token ? { "X-CSRF-Token": token } : {};
};

export const getSession = () => {
    try {
        return JSON.parse(localStorage.getItem(SESSION_KEY));
    } catch {
        return null;
    }
};

export const saveSession = (session) => {
    localStorage.setItem(SESSION_KEY, JSON.stringify({
        user: session.user,
        expires_at: session.expires_at,
    }));
    return session;
};

export const clearSession = () => {
    localStorage.removeItem(SESSION_KEY);
    forgetSiThoughtTraces();
    forgetBcoThoughtTraces();
};

export const displayName = () => getSession()?.user?.display_name || "";

/**
 * The sign-in endpoints answer with the step the user should now be on, for both
 * successes and rejections, so the caller reads the body either way. Only a
 * transport failure has no body of its own to show.
 */
const signInRequest = async (path, payload) => {
    try {
        const { data } = await client.post(path, payload);
        return data;
    } catch (error) {
        const data = error.response?.data;
        if (data?.error || data?.step !== undefined) {
            return { step: data.step ?? null, error: data.error || "", notice: "", ...data };
        }
        return {
            step: null,
            error: "We could not reach the server. Check your connection and try again.",
            notice: "",
        };
    }
};

export const submitCredentials = (userId, password) =>
    signInRequest("/auth/login", { user_id: userId, password });

export const submitNewPassword = (loginToken, password) =>
    signInRequest("/auth/password", { login_token: loginToken, password });

export const resendOtp = (loginToken) =>
    signInRequest("/auth/otp/resend", { login_token: loginToken });

export const verifyOtp = (loginToken, otp) =>
    signInRequest("/auth/otp/verify", { login_token: loginToken, otp });

export const getCurrentUser = async () => {
    const { data } = await client.get("/auth/me");
    return saveSession(data);
};

export const refreshSession = async () => {
    if (!refreshPromise) {
        refreshPromise = client
            .post("/auth/refresh", {}, { headers: csrfHeaders() })
            .then(({ data }) => saveSession(data))
            .catch((error) => {
                clearSession();
                window.dispatchEvent(new CustomEvent("auth-expired"));
                throw error;
            })
            .finally(() => {
                refreshPromise = null;
            });
    }
    return refreshPromise;
};

export const refreshSessionIfNeeded = async () => {
    const session = getSession();
    if (!session?.expires_at) return;

    const remainingMs = new Date(session.expires_at).getTime() - Date.now();
    if (remainingMs <= 0) {
        clearSession();
        window.dispatchEvent(new CustomEvent("auth-expired"));
        throw new Error("Session expired");
    }
    if (remainingMs <= REFRESH_BEFORE_MS) await refreshSession();
};

export const logout = async () => {
    try {
        await client.post("/auth/logout", {}, { headers: csrfHeaders() });
    } catch (error) {
        console.error("Error logging out:", error);
    } finally {
        clearSession();
    }
};

export const authenticatedFetch = async (url, options = {}) => {
    await refreshSessionIfNeeded();
    const method = (options.method || "GET").toUpperCase();
    const protectedHeaders = ["GET", "HEAD", "OPTIONS"].includes(method) ? {} : csrfHeaders();
    const response = await fetch(url, {
        ...options,
        credentials: "include",
        headers: {
            ...options.headers,
            ...protectedHeaders,
        },
    });
    if (response.status === 401) {
        clearSession();
        window.dispatchEvent(new CustomEvent("auth-expired"));
    }
    return response;
};

export const startActiveSessionRefresh = () => {
    const noteActivity = () => {
        lastActivityAt = Date.now();
    };
    ["mousedown", "keydown", "touchstart", "scroll"].forEach((eventName) => {
        window.addEventListener(eventName, noteActivity, { passive: true });
    });

    window.setInterval(() => {
        if (Date.now() - lastActivityAt <= ACTIVE_WITHIN_MS) {
            refreshSessionIfNeeded().catch(() => {
                window.dispatchEvent(new CustomEvent("auth-expired"));
            });
        }
    }, 60 * 1000);
};

export { csrfHeaders };
